<template>
    <hts-drawer
        v-model="visible"
        color="main"
        :rules="rulesAddTask"
        :submit="handleSubmit"
        size="large"
        :title="$t('labels.taskModule.add')"
        @close="closeOverSide">
        <div class="hts-vehicles-ignore-add-vehicle">
            <div class="hts-vehicles-ignore-add-vehicle__container-body">
                <hts-card :title="$t('labels.generalInformation')">
                    <hts-form-radio
                        v-model="dataAdd.taskType"
                        class="col-span-2"
                        :label="$t('labels.type')"
                        :items="taskTypes"
                        type="list"
                        required
                        value-key="key"
                        label-key="label" />

                    <task-name
                        v-model="dataAdd.type"
                        v-model:text="dataAdd.name"
                        :label="$t('labels.taskModule.name')"
                        :required="true"
                        :color="props.color"
                        name="taskModule.name"
                        prop="name" />

                    <hts-form-input
                        v-model="dataAdd.description"
                        :label="$t('labels.description')"
                        :color="props.color"
                        name="description"
                        prop="description" />

                    <div v-if="dataAdd.taskType === 'AFFECT_SHIPPING_TIME'">
                        <div class="flex">
                            <hts-form-checkbox
                                v-model="dataAdd.canSplitWay"
                                :checked="!!dataAdd.canSplitWay"
                                :items="[{ label: $t('labels.canSplitWay'), value: true }]" />

                            <hts-tooltip class="mt-1">
                                <div>
                                    <span class="font-bold">
                                        {{ $t('labels.canSplitWayInformation') }}
                                    </span>
                                </div>
                                <template #reference>
                                    <hi-information-circle-icon class="flex items-center text-green-500 w-6 h-6" />
                                </template>
                            </hts-tooltip>
                        </div>

                        <div class="flex">
                            <hts-form-checkbox
                                v-model="dataAdd.requireContainerNumber"
                                :checked="!!dataAdd.requireContainerNumber"
                                :items="[{ label: $t('labels.requireContainerNumber'), value: true }]" />
                        </div>

                        <div class="flex">
                            <hts-form-checkbox
                                v-model="dataAdd.documentRequired"
                                :checked="!!dataAdd.documentRequired"
                                :items="[{ label: $t('labels.documentRequired'), value: true }]" />
                        </div>

                        <div class="flex">
                            <hts-form-checkbox
                                v-model="dataAdd.taskStartEndDurationRequired"
                                :checked="!!dataAdd.taskStartEndDurationRequired"
                                :items="[{ label: $t('labels.taskStartEndDurationRequired'), value: true }]" />
                        </div>

                        <div class="flex">
                            <hts-form-checkbox
                                v-model="dataAdd.allowCustomerViewDocument"
                                :checked="!!dataAdd.allowCustomerViewDocument"
                                :items="[{ label: $t('labels.allowCustomerViewDocument'), value: true }]" />
                        </div>
                    </div>
                </hts-card>
                <hts-card
                    v-if="dataAdd.taskType === 'AFFECT_SHIPPING_TIME'"
                    :title="$t('labels.earliestLatest')">
                    <div
                        v-for="(timeCondition, index) in dataAdd.timeConditions"
                        :key="index">
                        <hr
                            v-if="index > 0"
                            class="my-4" >
                        <hts-form-input
                            v-model="timeCondition.name"
                            :label="$t('labels.earliestLatestName')"
                            :color="props.color"
                            :required="!!timeCondition.type" />
                        <hts-form-radio
                            v-model="timeCondition.type"
                            :items="timeConditionTypes"
                            value-key="key"
                            label-key="label"
                            :required="!!timeCondition.name"
                            class="w-full" />
                        <div class="grid grid-cols-2">
                            <div>
                                <hts-form-radio
                                    v-if="timeCondition.type === 'START'"
                                    v-model="timeCondition.timeType"
                                    class="ml-5"
                                    :items="timeConditionTimeTypes"
                                    value-key="key"
                                    label-key="label"
                                    required />
                            </div>
                            <div>
                                <hts-form-radio
                                    v-if="timeCondition.type === 'END'"
                                    v-model="timeCondition.timeType"
                                    class="ml-5"
                                    :items="timeConditionTimeTypes"
                                    value-key="key"
                                    label-key="label"
                                    required />
                            </div>
                        </div>

                        <div class="text-right">
                            <hts-form-button
                                size="small"
                                color="error"
                                @click="deleteTimeCondition(index)">
                                {{ $t('labels.delete') }}
                            </hts-form-button>
                        </div>
                    </div>

                    <hts-form-button
                        size="small"
                        @click="addTimeCondition">
                        {{ $t('labels.add') }}
                    </hts-form-button>
                </hts-card>

                <hts-card :title="$t('labels.completionTime')">
                    <div class="w-full overflow-x-auto">
                        <div class="min-w-[1024px]">
                            <hts-form-checkbox
                                v-model="dataAdd.completionTimeCategories"
                                :label="$t('labels.transportation')"
                                required
                                :items="transportationOptions()" />

                            <div class="flex flex-col gap-2">
                                <div class="grid-cols-4 grid gap-x-2">
                                    <p class="col-span-2 mb-2">
                                        {{ $t('labels.vehicleType') }}
                                    </p>
                                    <p :class="{ 'col-span-2': dataAdd.taskType === 'NO_AFFECT_SHIPPING_TIME' }">
                                        {{ $t('labels.averageCompletionTime') }} <span class="text-error-500">
                                            *
                                        </span>
                                    </p>
                                    <p v-if="dataAdd.taskType === 'AFFECT_SHIPPING_TIME'">
                                        {{ $t('labels.minimumCompletionTime') }} <span class="text-error-500">
                                            *
                                        </span>
                                    </p>
                                </div>
                                <template v-if="!dataAdd.completionTimeCategories || dataAdd.completionTimeCategories.includes(0)">
                                    <div
                                        v-for="(completionTimeVehicle, index) in dataAdd.completionTimes.vehicleCategory"
                                        :key="index"
                                        class="grid-cols-4 grid gap-x-2">
                                        <hts-form-input
                                            v-model="completionTimeVehicle.name"
                                            class="col-span-2"
                                            :color="color"
                                            disabled />
                                        <div :class="{ 'col-span-2': dataAdd.taskType === 'NO_AFFECT_SHIPPING_TIME' }">
                                            <hts-form-input
                                                v-model="completionTimeVehicle.averageTime"
                                                type="number"
                                                :color="color"
                                                required
                                                :number-decimal="1"
                                                :prop="`vehicleCategory-average-${completionTimeVehicle.id}`"
                                                name="averageCompletionTime"
                                                @change="(val: any) => completionTimeChange('vehicleCategory', index, val)" />
                                        </div>
                                        <div v-if="dataAdd.taskType === 'AFFECT_SHIPPING_TIME'">
                                            <hts-form-input
                                                v-model="completionTimeVehicle.minimumTime"
                                                type="number"
                                                :color="color"
                                                required
                                                :number-decimal="1"
                                                :prop="`vehicleCategory-minimum-${completionTimeVehicle.id}`"
                                                name="minimumCompletionTime"
                                                @change="minimumTimeChange('vehicleCategory', index)" />
                                        </div>
                                    </div>
                                </template>
                                <template v-if="!dataAdd.completionTimeCategories || dataAdd.completionTimeCategories.includes(1)">
                                    <div
                                        v-for="(completionTimeCont, index) in dataAdd.completionTimes.contCategory"
                                        :key="index"
                                        class="grid-cols-4 grid gap-x-2">
                                        <hts-form-input
                                            v-model="completionTimeCont.name"
                                            class="col-span-2"
                                            :color="color"
                                            disabled />
                                        <hts-form-input
                                            v-model="completionTimeCont.averageTime"
                                            :class="{ 'col-span-2': dataAdd.taskType === 'NO_AFFECT_SHIPPING_TIME' }"
                                            type="number"
                                            :color="color"
                                            required
                                            :number-decimal="1"
                                            :prop="`contCategory-average-${completionTimeCont.id}`"
                                            name="averageCompletionTime"
                                            @change="(val: any) => completionTimeChange('contCategory', index, val)" />
                                        <hts-form-input
                                            v-if="dataAdd.taskType === 'AFFECT_SHIPPING_TIME'"
                                            v-model="completionTimeCont.minimumTime"
                                            type="number"
                                            :color="color"
                                            required
                                            :number-decimal="1"
                                            :prop="`contCategory-minimum-${completionTimeCont.id}`"
                                            name="minimumCompletionTime"
                                            @change="minimumTimeChange('contCategory', index)" />
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </hts-card>
            </div>
        </div>
    </hts-drawer>
</template>
<script setup lang="ts">
import cloneDeep from 'lodash-es/cloneDeep'
import { initAddTask, ITask, ICompletionTime } from '../imports/form'
import data from '../imports/data'
import TaskName from './task-name.vue'
const { transportationOptions, transportationTypeKeys, contTypes, taskTypes, timeConditionTypes, timeConditionTimeTypes } = data()
const props = defineProps({
  color: {
    type: String,
    default: 'theme'
  },
  sizeSlideOver: {
    type: String,
    default: 'small'
  },
  sizeItemInput: {
    type: String,
    default: 'small'
  },
  column: {
    type: Number,
    default: 1
  }
})

const emits = defineEmits(['close', 'refreshTables', 'newTask'])

const visible = ref(false)

const open = async () => {
  dataAdd.value = { ...initAddTask }

  dataAdd.value.timeConditions = [
    {
      name: '',
      type: '',
      timeType: ''
    }
  ]
  await setInitEstimatedCompletionTime()
  visible.value = true
}

const closeOverSide = () => {
  visible.value = false
  resetField()
}

const dataAdd = ref<ITask>({ ...initAddTask })

const resetField = () => {
  dataAdd.value = { ...initAddTask }
  dataAdd.value.timeConditions = [
    {
      name: '',
      type: '',
      timeType: ''
    }
  ]
}

const addTimeCondition = () => {
  dataAdd.value.timeConditions.push({
    name: '',
    type: '',
    timeType: ''
  })
}

const deleteTimeCondition = (index: number) => {
  if (!dataAdd?.value?.timeConditions || !Array.isArray(dataAdd.value.timeConditions)) {
    return
  }

  if (dataAdd.value.timeConditions.length <= 1) {
    dataAdd.value.timeConditions = [
      {
        name: '',
        type: '',
        timeType: ''
      }
    ]
    return
  }

  dataAdd.value.timeConditions.splice(index, 1)
}

// reset thời gian tác vụ
const oldCompletionTimeCategories = ref([] as number[])
watch(
  () => dataAdd.value.completionTimeCategories,
  (val: number[]) => {
    if (oldCompletionTimeCategories.value.includes(0) && !val.includes(0)) {
      dataAdd.value.completionTimes.vehicleCategory = dataAdd.value.completionTimes.vehicleCategory.map((item: any) => {
        item.averageTime = null
        item.minimumTime = null
        item.minimumTimeChanged = false
        return item
      })
    }
    if (oldCompletionTimeCategories.value.includes(1) && !val.includes(1)) {
      dataAdd.value.completionTimes.contCategory = dataAdd.value.completionTimes.contCategory.map((item: any) => {
        item.averageTime = null
        item.minimumTime = null
        item.minimumTimeChanged = false
        return item
      })
    }
    oldCompletionTimeCategories.value = cloneDeep(val)
  },
  { deep: true }
)

const handleSubmit = async () => {
  const api = $urlAdmin.business.task.add
  const data: any = await $api(api, { body: dataAdd.value })
  const resData = data?.data?.value
  if (resData?.success) {
    emits('refreshTables')
    emits('newTask', resData.data)
    process.client && $toast().success($t('labels.addTaskSuccess'))
    closeOverSide()
    resetField()
  }
}

const completionTimeChange = (type: string, index: number, value: number) => {
  try {
    if (!['vehicleCategory', 'contCategory'].includes(type)) {
      return
    }
    if (dataAdd.value.completionTimes[type][index].minimumTimeChanged) {
      return
    }

    dataAdd.value.completionTimes[type][index].minimumTime = value
  } catch (e) {}
}

const minimumTimeChange = (type: string, index: number) => {
  try {
    if (!dataAdd.value.completionTimes[type][index].minimumTime) {
      dataAdd.value.completionTimes[type][index].minimumTimeChanged = false
      return
    }

    dataAdd.value.completionTimes[type][index].minimumTimeChanged = true
  } catch (e) {}
}

const setInitEstimatedCompletionTime = async () => {
  const completionTimeCont: ICompletionTime[] = []

  const completionTimeVehicle: ICompletionTime[] = []

  contTypes.forEach((item: { [key: string]: string | number }) => {
    completionTimeCont.push({
      id: item.value as number,
      name: item.label as string,
      averageTime: null,
      minimumTime: null,
      minimumTimeChanged: false
    })
  })
  dataAdd.value.completionTimes.contCategory = completionTimeCont

  const api = $urlAdmin.vehicle.category.getAll
  const res: any = await $api(api)
  const resData = res?.data?.value
  if (resData?.success) {
    const vehicleCategories = resData.data
    if (vehicleCategories && vehicleCategories.length) {
      vehicleCategories.forEach((item: { [key: string]: string | number }) => {
        completionTimeVehicle.push({
          id: item._id as string,
          name: item.name as string,
          averageTime: null,
          minimumTime: null,
          minimumTimeChanged: false
        })
      })
    }
  }

  dataAdd.value.completionTimes.vehicleCategory = completionTimeVehicle
}

const rulesAddTask = computed(() => {
  const rules = {
    name: 'required'
  } as { [key: string]: string }

  if (transportationTypeKeys.includes(0) && Array.isArray(dataAdd.value.completionTimes.vehicleCategory)) {
    dataAdd.value.completionTimes.vehicleCategory.forEach((type: any) => {
      rules[`vehicleCategory-average-${type.id}`] = `gt:0`
      rules[`vehicleCategory-minimum-${type.id}`] = `gt:0`
    })
  }
  if (transportationTypeKeys.includes(1) && Array.isArray(dataAdd.value.completionTimes.contCategory)) {
    dataAdd.value.completionTimes.contCategory?.forEach((type: any) => {
      rules[`contCategory-average-${type.id}`] = `gt:0`
      rules[`contCategory-minimum-${type.id}`] = `gt:0`
    })
  }
  return rules
})

defineExpose({
  open
})
</script>
